import { type ComponentType } from 'react';
import { Bell, CreditCard, Users, Zap } from '@/icons/Icon';
import type { NotificationType } from '@trulyinbox/types';
import type { NotificationCategory, NotificationDto } from '@/lib/notifications';
import { startOfDay, toDate } from '@/lib/date';

/**
 * Canonical per-category display data (label / icon). Single source for the
 * feed filter chips and the bell dropdown — so
 * a category's label or icon is defined once, not re-derived inline in each
 * component. Icon values are component *references* (rendered as `<Icon />` by
 * the consumer), which keeps this a pure non-component `.ts` module.
 */
export const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  MEMBERSHIP: 'Membership',
  BILLING: 'Billing',
  WARMUP: 'Warmup',
  SYSTEM: 'System',
};

export const CATEGORY_ICON: Record<
  NotificationCategory,
  ComponentType<{ size?: number }>
> = {
  MEMBERSHIP: Users,
  BILLING: CreditCard,
  WARMUP: Zap,
  SYSTEM: Bell,
};

/**
 * The one fix a notification points at, derived from its `type`. Any
 * notification about a mailbox (non-null `emailAccountId`) links to it —
 * "Reconnect mailbox" for auth failures, "View mailbox" otherwise. `null` = no
 * link (no billing fix and no mailbox). A deleted mailbox keeps its action but
 * carries `deletedAt`, so the link renders disabled with a reason instead of
 * vanishing or leading to a 404.
 * Upgrade targets the Plans tab, which `routes/billing.tsx` bounces a non-owner
 * to Overview from, so the link is safe to show every role.
 */
export type NotificationAction =
  | { kind: 'billing'; label: string; tab: 'plans' | 'overview' }
  | { kind: 'mailbox'; label: string; emailAccountId: number; deletedAt: string | null };

const UPGRADE_TYPES: ReadonlySet<NotificationType> = new Set<NotificationType>([
  'USAGE_LIMIT_APPROACHING',
  'USAGE_LIMIT_WARNING',
  'USAGE_LIMIT_CRITICAL',
  'USAGE_LIMIT_REACHED',
  'PLAN_DOWNGRADED',
]);

const RECONNECT_TYPES: ReadonlySet<NotificationType> = new Set<NotificationType>([
  'ACCOUNT_DISCONNECTED',
  'WARMUP_SUSPEND_MAX_DURATION',
]);

export function notificationAction(
  type: string,
  emailAccountId: number | null,
  emailAccountDeletedAt: string | null = null,
): NotificationAction | null {
  const t = type as NotificationType;
  if (UPGRADE_TYPES.has(t)) return { kind: 'billing', label: 'Upgrade plan', tab: 'plans' };
  if (t === 'PAYMENT_FAILED') return { kind: 'billing', label: 'Update payment', tab: 'overview' };
  if (emailAccountId == null) return null;
  const deletedAt = emailAccountDeletedAt;
  if (RECONNECT_TYPES.has(t)) return { kind: 'mailbox', label: 'Reconnect mailbox', emailAccountId, deletedAt };
  return { kind: 'mailbox', label: 'View mailbox', emailAccountId, deletedAt };
}

/**
 * One feed row: a notification plus any identical repeats folded into it
 * (rendered as a `×N` chip). `ids` holds every folded id so select / mark-read
 * act on the whole row, not just the visible lead.
 */
export type NotificationGroup = { lead: NotificationDto; ids: number[] };

function isRepeat(a: NotificationDto, b: NotificationDto): boolean {
  return (
    a.type === b.type &&
    a.title === b.title &&
    a.body === b.body &&
    a.emailAccountId === b.emailAccountId &&
    (a.readAt === null) === (b.readAt === null) &&
    startOfDay(toDate(a.createdAt)).getTime() === startOfDay(toDate(b.createdAt)).getTime()
  );
}

/**
 * Fold adjacent identical notifications (same type, text, mailbox, read state
 * and calendar day) into one row. Adjacent only — the feed is newest-first, so
 * an identical row separated by a different event stays its own row.
 */
export function collapseRepeats(rows: NotificationDto[]): NotificationGroup[] {
  const out: NotificationGroup[] = [];
  for (const n of rows) {
    const prev = out[out.length - 1];
    if (prev && isRepeat(prev.lead, n)) prev.ids.push(n.id);
    else out.push({ lead: n, ids: [n.id] });
  }
  return out;
}
